import { useState, useRef, useEffect } from "react";
import Markdown from "./Markdown";

const STORAGE_KEY = "charliegp-chat-history";
const MAX_STORED = 40;

const WELCOME = {
  role: "assistant",
  content:
    "Hi, I'm Charlie's agent. Ask me anything — like why you should hire him, what he's building, or what's on his blog.",
};

const STARTER_PROMPTS = [
  "Years of experience?",
  "Why hire Charlie?",
  "Tell me about TiiRADA",
  "Rails stack & background",
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <button
      type="button"
      className={"chat-msg-copy" + (copied ? " copied" : "")}
      onClick={onCopy}
      title={copied ? "Copied!" : "Copy response"}
      aria-label={copied ? "Copied" : "Copy response"}
    >
      {copied ? (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
    </button>
  );
}

function loadHistory() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    const valid = parsed.filter(
      (m) =>
        m &&
        typeof m.content === "string" &&
        (m.role === "user" || m.role === "assistant")
    );
    return valid.length ? valid : null;
  } catch {
    return null;
  }
}

export default function ChatAgent() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => loadHistory() || [WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const [kbOffset, setKbOffset] = useState(0);
  const [vvHeight, setVvHeight] = useState(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (open && typeof window !== "undefined" && window.innerWidth > 600) {
      inputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  useEffect(() => {
    if (!open) {
      setKbOffset(0);
      setVvHeight(null);
      return;
    }
    // Only apply keyboard-offset logic on mobile (bottom sheet); desktop sidebar
    // fills full height via CSS (top:0, bottom:0) and needs no JS height cap.
    if (typeof window === "undefined" || window.innerWidth > 600) return;

    const vv = window.visualViewport;
    if (!vv) return;

    const onResize = () => {
      const offset = Math.max(0, window.innerHeight - (vv.offsetTop + vv.height));
      setKbOffset(offset);
      setVvHeight(vv.height);
      if (offset > 0) {
        setTimeout(() => {
          bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 80);
      }
    };

    onResize();
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    return () => {
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
    };
  }, [open]);

  useEffect(() => {
    const handleOpenChat = () => setOpen(true);
    window.addEventListener("open-chat", handleOpenChat);
    return () => window.removeEventListener("open-chat", handleOpenChat);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    try {
      const history = messages.filter((m) => m.role !== "assistant" || m.content !== WELCOME.content);
      if (history.length === 0) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(history.slice(-MAX_STORED)));
      }
    } catch {
      /* storage unavailable — non-fatal */
    }
  }, [messages]);

  const resetChat = () => {
    setMessages([WELCOME]);
    setError(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  };

  const send = async (overrideText) => {
    const text = (typeof overrideText === "string" ? overrideText : input).trim();
    if (!text || loading) return;

    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    if (typeof overrideText !== "string") setInput("");
    setError(null);
    setLoading(true);

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: next }),
      });

      let data;
      try {
        data = await res.json();
      } catch {
        throw new Error(
          `Charlie's agent is busy (${res.status}). Please try again in a moment.`
        );
      }
      if (!res.ok) throw new Error(data.error || "Something went wrong");

      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setError(e.message || "Could not reach Charlie's agent. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <button
        className={"chat-fab" + (open ? " open" : "")}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat" : "Ask Charlie's agent"}
        title="Ask Charlie's agent"
      >
        <span className="chat-fab-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        </span>
        {!open && <span className="chat-fab-hint"></span>}
      </button>

      {open && (
        <>
          <div
            className="chat-backdrop"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            className="chat-panel"
            role="dialog"
            aria-label="Chat with Charlie's agent"
            data-keyboard-open={kbOffset > 40 ? "true" : "false"}
            style={{
              ...(kbOffset > 0 ? { bottom: `${kbOffset}px` } : {}),
              ...(vvHeight ? { maxHeight: `${Math.min(vvHeight - 12, 620)}px` } : {}),
            }}
          >
            <div className="chat-header">
              <span className="chat-title">Ask Charlie's agent</span>
              <span className="chat-status">{loading ? "typing…" : "online"}</span>
              <span className="chat-header-right">
                <button className="chat-reset" onClick={resetChat} title="Start a new conversation" aria-label="Start new conversation">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="1 4 1 10 7 10" />
                    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                  </svg>
                </button>
                <button className="chat-close" onClick={() => setOpen(false)} title="Close chat" aria-label="Close chat">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </span>
            </div>

            <div className="chat-body">
              {messages.map((m, i) => (
                <div key={i} className={"chat-msg " + m.role}>
                  <div className="chat-bubble">
                    <Markdown text={m.content} />
                    {m.role === "assistant" && m.content !== WELCOME.content && (
                      <CopyButton text={m.content} />
                    )}
                  </div>
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="chat-starters">
                  <span className="chat-starters-label">Suggested prompts:</span>
                  <div className="chat-chips-wrap">
                    {STARTER_PROMPTS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className="chat-starter-chip"
                        onClick={() => send(p)}
                        disabled={loading}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {loading && (
                <div className="chat-msg assistant">
                  <div className="chat-bubble chat-typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
              {error && <div className="chat-error">{error}</div>}
              <div ref={bottomRef} />
            </div>

            <div className="chat-input-row">
              <textarea
                ref={inputRef}
                className="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                onFocus={() => {
                  setTimeout(() => {
                    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
                  }, 100);
                }}
                rows={1}
                placeholder="Ask about Charlie…"
                aria-label="Message"
                enterKeyHint="send"
                autoCapitalize="sentences"
                autoComplete="off"
                spellCheck="true"
              />
              <button
                type="button"
                className="chat-send"
                onClick={() => send()}
                disabled={!input.trim() || loading}
                aria-label="Send message"
                title="Send message"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" fill="currentColor" fillOpacity="0.25" />
                </svg>
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
